import { FetchHttpClient, HttpClient } from 'effect/unstable/http'
import { createSTClientSDK, type RealtimeTransportFactory } from '@st/sdk'
import { Effect, Layer } from 'effect'
import { makeAnalyticsClient } from '~/features/analytics'
import type { AppType } from '~/features/app-type'
import { withAuthHttpClient } from '~/features/auth/st-auth-client'
import { Analytics, makeAnalyticsService } from './analytics'
import { AppFlags } from './app-flags'
import { Auth, authLiveLayer, authSdkFactoryLiveLayer } from './auth'
import { BrowserLifecycle, browserLifecycleLayer } from './browser-lifecycle'
import { BrowserCookies, browserCookiesLayer } from './browser-cookies'
import { browserDownloadsLayer } from './browser-downloads'
import { browserPageLayer } from './browser-page'
import { ErrorReporting, errorReportingLayer } from './error-reporting'
import { IdGenerator, idGeneratorLiveLayer } from './id-generator'
import { localTimeZoneLiveLayer } from './local-time-zone'
import { RuntimeConfig, runtimeConfigLayer } from './runtime-config'
import { RawSdk, sdkClientLayer } from './sdk-client'
import { SocketRuntimeConfig, socketLiveLayer } from './socket'
import { supportChatLayer } from './support-chat'
import { formConfigLiveLayer } from './form-config'
import { fileStorageLayer } from './file-storage'
import { xhrUploadLayer } from './xhr-upload'

export type AppRuntimeConfig = {
  appType: AppType
  browserOrigin: string
  devMode: boolean
}

type AuthSdkLayers = {
  runtime: Layer.Layer<RuntimeConfig>
  httpClient: Layer.Layer<HttpClient.HttpClient>
  errorReporting: Layer.Layer<ErrorReporting>
  idGenerator: Layer.Layer<IdGenerator>
  browserCookies: Layer.Layer<BrowserCookies>
}

/**
 * The environment may replace physical boundaries, but Auth and the SDK graph
 * are application logic and are always composed here. Keeping this graph shared
 * prevents tests from silently replacing request encoding, auth middleware, or
 * SDK error mapping with a second implementation.
 */
export function authSdkLayer(layers: AuthSdkLayers) {
  const authLive = authLiveLayer().pipe(
    Layer.provide(
      Layer.mergeAll(
        layers.runtime,
        layers.httpClient,
        layers.errorReporting,
        layers.idGenerator,
        layers.browserCookies,
        authSdkFactoryLiveLayer
      )
    )
  )

  const rawSdkLive = makeRawSdkLayer().pipe(
    Layer.provide(Layer.mergeAll(layers.runtime, layers.httpClient, layers.idGenerator, authLive))
  )

  const sdkClientLive = sdkClientLayer().pipe(
    Layer.provide(Layer.mergeAll(rawSdkLive, layers.errorReporting))
  )

  return Layer.mergeAll(authLive, rawSdkLive, sdkClientLive)
}

type AppSocketLayers = {
  runtime: Layer.Layer<RuntimeConfig>
  auth: Layer.Layer<Auth>
  errorReporting: Layer.Layer<ErrorReporting>
  browserLifecycle: Layer.Layer<BrowserLifecycle>
  transport?: RealtimeTransportFactory
}

/** The realtime runtime is real in every environment; only its wire transport varies. */
export function appSocketLayer(layers: AppSocketLayers) {
  const socketConfig = Layer.effect(
    SocketRuntimeConfig,
    Effect.gen(function* () {
      const runtime = yield* RuntimeConfig
      const auth = yield* Auth
      const errorReporting = yield* ErrorReporting

      return {
        endpoint: runtime.socketEndpoint,
        getCsrfToken: auth.getHeaders().pipe(
          Effect.flatMap((headers) => {
            const csrfToken = headers['x-csrf-token']
            if (!csrfToken) {
              return Effect.fail(new Error('missing csrf token in auth headers'))
            }
            return Effect.succeed(csrfToken)
          })
        ),
        makeTransport: layers.transport,
        onSocketErrored: (event) =>
          errorReporting.addBreadcrumb({
            category: 'socket',
            level: 'warning',
            message: 'realtime socket errored',
            data: event
          }),
        onTopicJoinStalled: (event) => {
          if (!event.topic.startsWith('folder:')) {
            return Effect.void
          }

          return errorReporting.captureEvent({
            message: 'Folder channel stalled',
            level: 'warning',
            extra: event
          })
        }
      }
    })
  ).pipe(Layer.provide(Layer.mergeAll(layers.runtime, layers.auth, layers.errorReporting)))

  return socketLiveLayer().pipe(
    Layer.provide(Layer.mergeAll(socketConfig, layers.browserLifecycle))
  )
}

export function appRuntimeLayer(config: AppRuntimeConfig) {
  const runtimeLayer = runtimeConfigLayer(config)

  const fetchHttpClientLayer = FetchHttpClient.layer.pipe(
    Layer.provideMerge(
      Layer.succeed(FetchHttpClient.RequestInit, {
        credentials: 'include'
      })
    )
  )

  const errorReportingLiveLayer = errorReportingLayer()
  const idGeneratorLive = idGeneratorLiveLayer
  const browserCookiesLive = browserCookiesLayer
  const authSdkLive = authSdkLayer({
    runtime: runtimeLayer,
    httpClient: fetchHttpClientLayer,
    errorReporting: errorReportingLiveLayer,
    idGenerator: idGeneratorLive,
    browserCookies: browserCookiesLive
  })

  const appFlagsLive = Layer.succeed(AppFlags, { devMode: config.devMode })

  const analyticsLive = Layer.effect(
    Analytics,
    Effect.gen(function* () {
      const runtime = yield* RuntimeConfig
      const sdk = yield* RawSdk
      return makeAnalyticsService(makeAnalyticsClient(runtime.appType), sdk)
    })
  ).pipe(Layer.provideMerge(runtimeLayer), Layer.provide(authSdkLive))

  const supportChatLive = supportChatLayer(config.appType)
  const browserLifecycleLive = browserLifecycleLayer().pipe(Layer.provide(errorReportingLiveLayer))

  const xhrUploadLive = xhrUploadLayer()
  const fileStorageLive = fileStorageLayer().pipe(
    Layer.provide(Layer.mergeAll(authSdkLive, xhrUploadLive))
  )

  const socketLive = appSocketLayer({
    runtime: runtimeLayer,
    auth: authSdkLive,
    errorReporting: errorReportingLiveLayer,
    browserLifecycle: browserLifecycleLive
  })

  return Layer.mergeAll(
    runtimeLayer,
    appFlagsLive,
    browserCookiesLive,
    browserDownloadsLayer,
    browserPageLayer,
    idGeneratorLive,
    localTimeZoneLiveLayer,
    authSdkLive,
    analyticsLive,
    supportChatLive,
    errorReportingLiveLayer,
    xhrUploadLive,
    fileStorageLive,
    browserLifecycleLive,
    socketLive,
    formConfigLiveLayer().pipe(Layer.provide(Layer.mergeAll(runtimeLayer, fetchHttpClientLayer)))
  )
}

function makeRawSdkLayer() {
  return Layer.effect(
    RawSdk,
    Effect.gen(function* () {
      const runtime = yield* RuntimeConfig
      const httpClient = yield* HttpClient.HttpClient
      const auth = yield* Auth
      const idGenerator = yield* IdGenerator

      return createSTClientSDK({
        baseUrl: runtime.apiBaseUrl,
        getRequestHeaders: () => {
          const headers: Record<string, string> = {}
          if (runtime.clientVersion) {
            headers['x-client-version'] = runtime.clientVersion
          }
          return Effect.succeed(headers)
        },
        httpClient: withAuthHttpClient({
          httpClient,
          authClient: auth
        }),
        makeRequestId: idGenerator.generate
      })
    })
  )
}
